import React, { useCallback, useEffect, useState } from 'react';
import { testBtnOutline, testBtnPrimary, testBtnSecondary } from './testUiClasses';

const FADE_MS = 450;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function AbilityCoverPage({
  title,
  eyebrow,
  children,
  primaryLabel,
  onPrimary,
  restartLabel,
  onRestart,
  afterActions,
  secondaryLabel,
  onSecondary,
  overlay = true,
  fullBleed = false,
  wide = false,
  visual = null,
  primaryClassName = testBtnPrimary
}) {
  const [entered, setEntered] = useState(prefersReducedMotion());
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const handlePrimary = useCallback(() => {
    if (exiting) return;
    if (prefersReducedMotion()) {
      onPrimary();
      return;
    }
    setExiting(true);
    window.setTimeout(() => onPrimary(), FADE_MS);
  }, [exiting, onPrimary]);

  const motionClass = exiting
    ? 'opacity-0 translate-y-2 scale-[0.99]'
    : entered
      ? 'opacity-100 translate-y-0 scale-100'
      : 'opacity-0 translate-y-3 scale-[0.99]';

  const actionButtons = (className = '') => (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <button
        type="button"
        onClick={handlePrimary}
        disabled={exiting}
        className={primaryClassName}
      >
        {primaryLabel}
      </button>
      {restartLabel && onRestart ? (
        <button
          type="button"
          onClick={onRestart}
          disabled={exiting}
          className={`${testBtnOutline} px-8 py-4 text-base shadow-sm focus:ring-offset-canvas`}
        >
          {restartLabel}
        </button>
      ) : null}
      {secondaryLabel && onSecondary ? (
        <button
          type="button"
          onClick={onSecondary}
          disabled={exiting}
          className={testBtnSecondary}
        >
          {secondaryLabel}
        </button>
      ) : null}
    </div>
  );

  const panel = (
    <div
      className={`relative w-full transition-all duration-500 ease-out ${motionClass} ${
        overlay && !fullBleed ? 'max-w-3xl' : ''
      } ${fullBleed ? (wide ? 'max-w-6xl' : 'max-w-3xl') : ''}`}
    >
      <div
        className={
          fullBleed
            ? 'relative'
            : 'relative overflow-hidden rounded-2xl border border-edge bg-surface/95 backdrop-blur-xl shadow-2xl shadow-black/10 ring-1 ring-black/5 dark:shadow-black/40 dark:ring-white/5'
        }
      >
        {!fullBleed ? (
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_42%,rgb(var(--edvise-canvas)/0.72)_100%)]"
            aria-hidden
          />
        ) : null}
        <div className={`relative ${fullBleed ? (wide ? 'px-4 py-8 md:px-6 md:py-10' : 'px-2 py-8 text-center md:py-12') : 'p-6 md:p-8'}`}>
          {wide && fullBleed ? (
            <>
              <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.08fr)_minmax(24rem,0.92fr)] lg:gap-16">
                <div className="text-center lg:text-left">
                  {eyebrow ? (
                    <p className="mb-5 text-sm font-semibold uppercase tracking-[0.13em] text-link">
                      {eyebrow}
                    </p>
                  ) : null}
                  <h1 className="mb-8 font-montserrat text-5xl font-bold tracking-[-0.045em] text-ink sm:text-6xl lg:text-7xl">
                    {title}
                  </h1>
                  <div className="mb-9">{children}</div>
                  {actionButtons('justify-center lg:justify-start')}
                </div>
                {visual}
              </div>
              {afterActions ? <div className="mt-12 border-t border-edge pt-8 md:mt-14">{afterActions}</div> : null}
            </>
          ) : (
            <>
              {eyebrow ? (
                <p className="text-sm font-semibold uppercase tracking-wide text-link mb-2">
                  {eyebrow}
                </p>
              ) : null}
              <h1
                className={`font-bold text-ink font-montserrat ${
                  fullBleed ? 'mb-5 text-4xl md:text-5xl' : 'mb-6 text-2xl md:text-3xl'
                }`}
              >
                {title}
              </h1>
              <div className="mb-8">{children}</div>
              {actionButtons(fullBleed ? 'justify-center' : '')}
              {afterActions ? <div className="mt-9">{afterActions}</div> : null}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (!overlay) {
    return panel;
  }

  return (
    <div
      className={`relative flex w-full justify-center -mx-4 md:-mx-8 px-4 md:px-8 py-6 md:py-10 ${
        wide && fullBleed ? 'items-start' : fullBleed ? 'items-start' : 'items-center'
      } ${fullBleed ? 'flex-1 min-h-0' : 'min-h-[min(72vh,calc(100vh-10rem))]'}`}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_75%_at_50%_45%,rgb(var(--edvise-surface)/0.95)_0%,rgb(var(--edvise-canvas)/0.55)_50%,rgb(var(--edvise-canvas)/0.08)_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-canvas/80 via-transparent to-canvas/90"
        aria-hidden
      />
      {panel}
    </div>
  );
}

export default AbilityCoverPage;
